// File location: components/client/ClientProfileForm.tsx

import React, { useEffect } from "react";
import { zoullooColors } from "../../theme";
import {
	Box,
	Button,
	Typography,
	Paper,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Snackbar,
	Alert
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { useCreateCompany, useAddCompanyAddress } from "../../hooks/useCompany";
import { useQueryClient } from "@tanstack/react-query";
import CompanyForm from "../company/CompanyForm";
import { CompanyCreateDTO, CompanyAddressCreateDTO } from "../../models/Company";
import { Client } from "../../models/Client";
import apiClient from "../../api/api-client";

// Import our refactored components
import CompanyAutocomplete from "../common/CompanyAutocomplete";
import PhoneInput from "../UI/PhoneInput";
import {
	BasicInfoSection,
	ExpertiseSection,
	CommunicationSection
} from "./form-components/FormSections";
import { useClientForm } from "./hooks/useClientForm";

/**
 * Props for ClientProfileForm component
 */
interface ClientProfileFormProps {
	isOnboarding?: boolean;
	clientToEdit?: Client;
}

/**
 * Component for client profile form - used for both onboarding and profile updating
 */
const ClientProfileForm: React.FC<ClientProfileFormProps> = ({
	isOnboarding = false,
	clientToEdit
}) => {
	const { t } = useTranslation();

	// Hooks for company and address creation
	const createCompanyMutation = useCreateCompany();
	const addCompanyAddressMutation = useAddCompanyAddress();
	const queryClient = useQueryClient();

	// Use our custom hook for form logic
	const {
		// Form data
		firstName,
		lastName,
		email,
		jobTitle,
		phoneNumber,
		selectedFrequency,
		usesPlatforms,
		selectedCommunicationChannels,
		linkedInUrl,
		selectedExpertises,
		selectedDomains,
		selectedTools,

		// Company-related
		selectedCompany,
		companyDialogOpen,
		createdCompanyIdRef,

		// UI state
		errors,
		alertOpen,
		alertMessage,
		alertSeverity,
		isLoading,
		isMutating,
		dataLoadingComplete,
		isPhoneRequired,

		// Data
		expertises,
		tools,
		businessDomains,
		communicationChannels,
		frequencies,

		// Handlers
		handleFieldChange,
		handleSubmit,
		setSelectedCompany,
		setCompanyDialogOpen,
		setAlertOpen,
		setAlertMessage,
		setAlertSeverity,
		getExpertiseNameById,
		getToolNameById,
		canChangeCompany,
		canCreateNewCompany,
		hasCreatedCompany
	} = useClientForm({ isOnboarding, clientToEdit });

	// If a company was created, update companyId and selected company
	useEffect(() => {
		if (createdCompanyIdRef.current && !companyDialogOpen) {
			handleFieldChange("companyId", createdCompanyIdRef.current);
			// Fetch the created company to set it as selected
			const fetchCreatedCompany = async () => {
				try {
					const response = await apiClient.get(`/companies/${createdCompanyIdRef.current}`);
					setSelectedCompany(response.data);
					createdCompanyIdRef.current = null;
				} catch (error) {
					console.error("Error fetching created company:", error);
				}
			};
			fetchCreatedCompany();
		}
	}, [companyDialogOpen, handleFieldChange, setSelectedCompany]);

	const handleCompanyDialogClose = () => {
		setCompanyDialogOpen(false);
	};

	const handleAlertClose = () => {
		setAlertOpen(false);
	};

	// Handle company creation using the proper hooks
	const handleCreateCompany = async (
		companyData: CompanyCreateDTO,
		addressData?: any
	) => {
		try {
			// Create the company using the proper hook
			const createdCompany = await createCompanyMutation.mutateAsync(companyData);

			// Address is now required, so we should always have address data
			if (addressData && addressData.fullAddress && createdCompany?.id) {
				// Create the address association data
				const addressCreateData: CompanyAddressCreateDTO = {
					companyId: createdCompany.id,
					address: addressData,
					addressTypeCode: "HEADQUARTERS",
					isDefault: true
				};

				// Use the proper hook to add the address
				await addCompanyAddressMutation.mutateAsync(addressCreateData);
			}

			// Store the created company ID to be set after dialog closes
			createdCompanyIdRef.current = createdCompany.id;
			
			// Invalidate the user companies count cache to refresh the limit check
			queryClient.invalidateQueries({ queryKey: ["userCreatedCompaniesCount"] });

			// Set success message
			setAlertMessage(t("company.createSuccess"));
			setAlertSeverity("success");
			setAlertOpen(true);

			// Close the dialog
			setCompanyDialogOpen(false);
		} catch (error) {
			console.error("Error creating company:", error);
			setAlertMessage(t("company.createError"));
			setAlertSeverity("error");
			setAlertOpen(true);
		}
	};

	// Determine if Insight tools should be shown
	const showInsightTools = selectedExpertises.some(
		expId => expertises?.find(e => e.id === expId)?.type === "Insight"
	);


	// Show loading indicator while data is loading
	if (isLoading || !dataLoadingComplete) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Paper elevation={0} sx={{ p: 4 }}>
			<Typography variant="h2" component="h1" sx={{ mb: 3, fontWeight: "bold" }}>
				{isOnboarding
					? t("clientProfile.completeProfile")
					: t("clientProfile.profileDetails")}
			</Typography>


			<form onSubmit={handleSubmit} noValidate>
				<Grid container spacing={3}>
					{/* Basic Information Section */}
					<BasicInfoSection
						firstName={firstName}
						lastName={lastName}
						email={email}
						jobTitle={jobTitle}
						errors={errors}
						onChange={handleFieldChange}
					/>

					{/* Company Selection */}
					<Grid size={{ xs: 12, md: 6 }}>
						<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
							{t('project.company')} *
						</Typography>
						<CompanyAutocomplete
							value={selectedCompany}
							error={errors.companyId}
							required
							label=""
							disabled={selectedCompany !== null && !canChangeCompany}
							helperText={
								selectedCompany !== null && !canChangeCompany
									? isOnboarding
										? t("clientProfile.companyAssignedByZoulloo")
										: t("clientProfile.companyChangeDisabled")
									: !canCreateNewCompany && hasCreatedCompany
									? t("clientProfile.companyCreationLimitReached")
									: undefined
							}
							showCreateButton={canChangeCompany && canCreateNewCompany}
							onChange={company => {
								setSelectedCompany(company);
								handleFieldChange("companyId", company?.id ?? null);
							}}
							onCreateNew={() => (canChangeCompany && canCreateNewCompany ? setCompanyDialogOpen(true) : null)}
						/>
					</Grid>

					{/* Phone Input */}
					<Grid size={{ xs: 12, md: 6 }}>
						<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
							{t('clientProfile.phoneNumber')}{isPhoneRequired ? ' *' : ''}
						</Typography>
						<PhoneInput
							value={phoneNumber || ""}
							onChange={(phone: string) => {
								handleFieldChange("phoneNumber", phone);
							}}
							name="phoneNumber"
							error={!!errors.phoneNumber}
							helperText={errors.phoneNumber}
							required={isPhoneRequired}
							hideLabel={true}
						/>
					</Grid>

					{/* Expertise Section */}
					<ExpertiseSection
						selectedExpertises={selectedExpertises}
						selectedFrequency={selectedFrequency}
						selectedDomains={selectedDomains}
						selectedTools={selectedTools}
						usesPlatforms={usesPlatforms}
						showInsightTools={showInsightTools}
						errors={errors}
						expertises={expertises ?? []}
						frequencies={frequencies ?? []}
						businessDomains={businessDomains ?? []}
						tools={tools ?? []}
						onExpertisesChange={value => handleFieldChange("selectedExpertises", value)}
						onFrequencyChange={value => handleFieldChange("selectedFrequency", value)}
						onDomainsChange={value => handleFieldChange("selectedDomains", value)}
						onToolsChange={value => handleFieldChange("selectedTools", value)}
						onUsesPlatformsChange={value => handleFieldChange("usesPlatforms", value)}
						getExpertiseNameById={getExpertiseNameById}
						getToolNameById={getToolNameById}
					/>

					{/* Communication Section */}
					<CommunicationSection
						communicationChannels={communicationChannels ?? []}
						selectedCommunicationChannels={selectedCommunicationChannels}
						linkedInUrl={linkedInUrl}
						errors={errors}
						onChannelsChange={value => handleFieldChange("communicationChannels", value)}
						onLinkedInChange={value => handleFieldChange("linkedInUrl", value)}
					/>

					{/* Submit buttons */}
					<Grid size={12} sx={{ mt: 3, display: "flex", gap: 2 }}>
						<Button
							type="submit"
							variant="contained"
							size="large"
							disabled={isMutating}
							sx={{
								backgroundColor: zoullooColors.secondary.main,
								color: zoullooColors.secondary.contrastText,
								"&:hover": {
									backgroundColor: zoullooColors.secondary.dark
								},
								"&.Mui-disabled": {
									backgroundColor: zoullooColors.grey[500]
								},
								px: 4,
								py: 1.5
							}}
						>
							{isMutating ? (
								<CircularProgress size={24} color="inherit" />
							) : isOnboarding ? (
								t("clientProfile.completeOnboarding")
							) : (
								t("common.save")
							)}
						</Button>
					</Grid>
				</Grid>
			</form>

			{/* Company Creation Dialog */}
			<Dialog
				open={companyDialogOpen}
				onClose={handleCompanyDialogClose}
				maxWidth="lg"
				fullWidth
			>
				<DialogTitle sx={{ color: zoullooColors.secondary.main }}>{t("clientProfile.createNewCompany")}</DialogTitle>
				<DialogContent dividers>
					<CompanyForm
						initialData={{}}
						onSubmit={handleCreateCompany}
						isSubmitting={
							createCompanyMutation.isPending || addCompanyAddressMutation.isPending
						}
						submitButtonText={t("company.create")}
						isClientOnboarding={true}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCompanyDialogClose}>{t("common.cancel")}</Button>
				</DialogActions>
			</Dialog>

			{/* Alert Snackbar */}
			<Snackbar
				open={alertOpen}
				autoHideDuration={5000}
				onClose={handleAlertClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleAlertClose}
					severity={alertSeverity}
					variant="filled"
					sx={{ width: "100%" }}
				>
					{alertMessage}
				</Alert>
			</Snackbar>
		</Paper>
	);
};

export default ClientProfileForm;
